import { useState, useEffect, useMemo, useCallback } from "react";
import { AlertTriangle, Plus, Edit2, Trash2, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import DatePickerField from "@/components/DatePickerField";
import ExportBar from "@/components/ExportBar";
import ReceiptUploader from "@/components/ReceiptUploader";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCurrency } from "@/lib/formatters";
import * as db from "@/lib/db";

const emptyAdj = (year) => ({
  id: null,
  assessmentYear: String(year),
  adjustmentTypeId: "",
  adjustmentDate: new Date().toISOString().slice(0, 10),
  description: "",
  amount: "",
  notes: "",
});

const TaxPreparation = () => {
  const today = new Date();
  const [year, setYear] = useState(String(today.getFullYear()));
  const [availableYears, setAvailableYears] = useState([]);
  const [types, setTypes] = useState([]);
  const [adjustmentTypes, setAdjustmentTypes] = useState([]);
  const [taxTreatments, setTaxTreatments] = useState([]);
  const [events, setEvents] = useState([]);
  const [txs, setTxs] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdjForm, setShowAdjForm] = useState(false);
  const [adjForm, setAdjForm] = useState(emptyAdj(today.getFullYear()));
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchStatic = useCallback(async () => {
    try {
      const [tt, adjT, tax, years] = await Promise.all([
        db.listTransactionTypes({ includeArchived: true }),
        db.listTaxAdjustmentTypes({ includeArchived: true }),
        db.listTaxTreatmentStatuses({ includeArchived: true }),
        db.listEventYears(),
      ]);
      setTypes(tt); setAdjustmentTypes(adjT); setTaxTreatments(tax);
      const yset = new Set(years);
      yset.add(String(today.getFullYear()));
      setAvailableYears(Array.from(yset).sort((a, b) => b.localeCompare(a)));
    } catch (e) { console.error(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchYear = useCallback(async () => {
    try {
      setLoading(true);
      const [evs, ts, adj] = await Promise.all([
        db.listEventsSummary({ year, sortBy: "latest", eventStatus: "completed" }),
        db.listTransactions({ from: `${year}-01-01`, to: `${year}-12-31`, sortBy: "date_asc" }),
        db.listTaxAdjustments(year),
      ]);
      setEvents(evs); setTxs(ts); setAdjustments(adj);
    } catch (e) { console.error(e); toast.error("Failed to load tax data"); }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchStatic(); }, [fetchStatic]);
  useEffect(() => { fetchYear(); }, [fetchYear]);

  const typeById = useCallback((id) => types.find((t) => t.id === id), [types]);
  const taxById  = useCallback((id) => taxTreatments.find((t) => t.id === id), [taxTreatments]);
  const adjTypeById = useCallback((id) => adjustmentTypes.find((t) => t.id === id), [adjustmentTypes]);

  const totals = useMemo(() => {
    // From events
    let revenue = 0, productCost = 0, eventExpenses = 0;
    events.forEach((e) => {
      revenue     += Number(e.totalSalesCollected || 0);
      productCost += Number(e.totalProductCost || 0);
      eventExpenses += Number(e.rentalCost || 0) + Number(e.transportCost || 0) + Number(e.otherCost || 0) + Number(e.totalStaffWages || 0);
    });
    // From transactions bucketed by pl_treatment and tax_treatment
    let allowable = 0, nonDeductible = 0, partly = 0;
    let opEx = 0, woodIncentive = 0;
    let productPurchases = 0, assetPurchases = 0;
    let ownerDraws = 0;
    let pendingCount = 0;
    let missingReceiptCount = 0;
    txs.forEach((t) => {
      if (t.receiptStatus === "Missing") missingReceiptCount++;
      const tp = typeById(t.transactionTypeId);
      const tax = taxById(t.taxTreatmentId);
      const amt = Number(t.amount || 0);
      const pl = tp?.plTreatment;
      // Bucket by pl_treatment
      if (pl === "operating_expense") {
        opEx += amt;
        if ((tp?.name || "").toLowerCase().includes("wood")) woodIncentive += amt;
      } else if (pl === "product_purchase_cash_only") productPurchases += amt;
      else if (pl === "asset_capitalized") assetPurchases += amt;
      else if (pl === "owner_draw") ownerDraws += amt;
      else if (pl === "pending") pendingCount++;
      // Bucket by tax_treatment (only if it applies)
      const tName = (tax?.name || "").toLowerCase();
      if (tName === "allowable business expense") allowable += amt;
      else if (tName === "non-deductible") nonDeductible += amt;
      else if (tName === "partly business use") partly += (amt * (Number(t.businessUsePercentage ?? 100) / 100));
    });
    const grossProfit = revenue - productCost;
    const accountingNetProfit = revenue - productCost - eventExpenses - opEx;
    // Tax adjustments — positive amount = add-back (increases taxable income),
    //                    negative amount = deduction (reduces taxable income).
    const totalAddBacks = adjustments.filter((a) => a.amount > 0).reduce((s, a) => s + a.amount, 0);
    const totalDeductions = adjustments.filter((a) => a.amount < 0).reduce((s, a) => s + a.amount, 0); // already negative
    const estimatedAdjustedBusinessIncome = accountingNetProfit + totalAddBacks + totalDeductions;
    return {
      revenue, productCost, grossProfit,
      eventExpenses, opEx, woodIncentive,
      accountingNetProfit,
      allowable, nonDeductible, partly,
      productPurchases, assetPurchases, ownerDraws,
      pendingCount, missingReceiptCount,
      totalAddBacks, totalDeductions,
      estimatedAdjustedBusinessIncome,
    };
  }, [events, txs, adjustments, typeById, taxById]);

  const openNewAdj = () => { setAdjForm(emptyAdj(year)); setShowAdjForm(true); };
  const openEditAdj = (a) => {
    setAdjForm({
      id: a.id,
      assessmentYear: String(a.assessmentYear),
      adjustmentTypeId: a.adjustmentTypeId,
      adjustmentDate: a.adjustmentDate,
      description: a.description || "",
      amount: String(a.amount),
      notes: a.notes || "",
    });
    setShowAdjForm(true);
  };
  const setAdjField = (k, v) => setAdjForm((f) => ({ ...f, [k]: v }));

  const saveAdj = async () => {
    if (!adjForm.assessmentYear) { toast.error("Assessment year is required"); return; }
    if (!adjForm.adjustmentTypeId) { toast.error("Adjustment type is required"); return; }
    if (!adjForm.adjustmentDate) { toast.error("Date is required"); return; }
    if (adjForm.amount === "" || isNaN(parseFloat(adjForm.amount))) { toast.error("Amount is required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...adjForm, amount: parseFloat(adjForm.amount) };
      if (adjForm.id) { await db.updateTaxAdjustment(adjForm.id, payload); toast.success("Adjustment updated"); }
      else { await db.createTaxAdjustment(payload); toast.success("Adjustment added"); }
      setShowAdjForm(false);
      fetchYear();
    } catch (e) { console.error(e); toast.error(e.message || "Could not save adjustment"); }
    finally { setSubmitting(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await db.deleteTaxAdjustment(deleteTarget.id);
      toast.success("Adjustment deleted");
      setDeleteTarget(null); fetchYear();
    } catch { toast.error("Failed to delete"); }
  };

  const selectClass = "w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm text-slate-800 appearance-none cursor-pointer transition-colors";
  const inputClass = "bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm";
  const labelClass = "text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block";

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="tax-prep-title">
              Annual Tax Preparation Summary
            </h1>
            <p className="text-sm text-slate-600 mt-1">LHDN filing prep — record & estimation only</p>
          </div>
          <div>
            <label className={labelClass}>Assessment Year</label>
            <select value={year} onChange={(e) => setYear(e.target.value)} className={`${selectClass} w-56`} data-testid="tax-year-select">
              {availableYears.map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
            <div className="mt-3">
              <ExportBar
                testIdPrefix="tax-export"
                buildReport={() => {
                  const headCols = [{ key: "label", label: "Line" }, { key: "value", label: "Amount (RM)", type: "currency" }];
                  const headRows = [
                    { label: "Annual Revenue", value: totals.revenue },
                    { label: "Less: Product Cost", value: -totals.productCost },
                    { label: "Gross Profit", value: totals.grossProfit },
                    { label: "Accounting Net Profit", value: totals.accountingNetProfit },
                    { label: "+ Add-back Adjustments", value: totals.totalAddBacks },
                    { label: "+ Allowable Adjustments / Deductions", value: totals.totalDeductions },
                    { label: "Estimated Adjusted Business Income", value: totals.estimatedAdjustedBusinessIncome },
                  ];
                  const brkRows = [
                    { label: "Allowable Business Expenses", value: totals.allowable },
                    { label: "Non-deductible Expenses", value: totals.nonDeductible },
                    { label: "Partly Business-use (weighted)", value: totals.partly },
                    { label: "Owner Withdrawals (not deducted)", value: totals.ownerDraws },
                    { label: "Product Purchases (cash view only)", value: totals.productPurchases },
                    { label: "Equipment / Asset Purchases (capitalised)", value: totals.assetPurchases },
                    { label: "Wood Profit-based Incentive (in Op. Exp.)", value: totals.woodIncentive },
                    { label: "Missing Receipts (count)", value: totals.missingReceiptCount },
                    { label: "Pending Review Transactions (count)", value: totals.pendingCount },
                  ];
                  const adjCols = [
                    { key: "adjustmentDate", label: "Date" },
                    { key: "typeName", label: "Type" },
                    { key: "description", label: "Description" },
                    { key: "amount", label: "Amount (RM)", type: "currency" },
                    { key: "notes", label: "Notes" },
                  ];
                  const adjRows = adjustments.map((a) => ({ ...a, typeName: adjTypeById(a.adjustmentTypeId)?.name || "" }));
                  return {
                    title: `Annual Tax Preparation Summary — YA ${year}`,
                    subtitle: `Record preparation only — not for LHDN submission`,
                    filename: `Gao-Chill-Tax-Preparation-YA${year}`,
                    columns: adjCols,
                    rows: adjRows,
                    sections: [
                      { heading: "Headline Figures", columns: headCols, rows: headRows },
                      { heading: "Breakdown", columns: headCols, rows: brkRows },
                      { heading: `Tax Adjustments (${adjustments.length})`, columns: adjCols, rows: adjRows },
                    ],
                    metrics: [
                      { label: "Accounting Net Profit", value: formatCurrency(totals.accountingNetProfit) },
                      { label: "Estimated Adjusted Business Income", value: formatCurrency(totals.estimatedAdjustedBusinessIncome) },
                    ],
                    footnote: "IMPORTANT: This report is for record preparation and estimation only. It does not submit any LHDN return. Estimated Adjusted Business Income may require your own review. Tax treatment and capital-allowance decisions remain editable. This system does not guarantee that any figure is the final taxable income accepted by LHDN.",
                  };
                }}
              />
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-4 text-sm text-red-800 space-y-1.5" data-testid="tax-disclaimer">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">For record preparation and estimation only.</p>
              <ul className="mt-1 space-y-1 text-xs list-disc list-inside text-red-700">
                <li>This report does <strong>not</strong> automatically file or submit any LHDN return.</li>
                <li>Estimated Adjusted Business Income may require your own review.</li>
                <li>Tax treatment and capital-allowance decisions remain editable at any time.</li>
                <li>This system does <strong>not</strong> guarantee that any figure is the final taxable income accepted by LHDN.</li>
              </ul>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Calculating…</div>
        ) : (
          <>
            {/* Headline block */}
            <section className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] text-white rounded-2xl p-5 sm:p-6 shadow-lg space-y-4" data-testid="tax-headline">
              <TaxRow label="Annual Revenue" value={totals.revenue} big testId="tax-revenue" />
              <TaxRow label="Less: Product Cost" value={-totals.productCost} testId="tax-product-cost" />
              <TaxRow label="Gross Profit" value={totals.grossProfit} bold testId="tax-gross-profit" />
              <div className="border-t border-white/20 pt-3">
                <TaxRow label="Accounting Net Profit" value={totals.accountingNetProfit} bold big
                  color={totals.accountingNetProfit >= 0 ? "text-emerald-300" : "text-red-300"}
                  testId="tax-accounting-net" />
              </div>
              <div className="border-t border-white/20 pt-3 space-y-2">
                <TaxRow label="+ Add-back Adjustments" value={totals.totalAddBacks} testId="tax-addbacks" />
                <TaxRow label="+ Allowable Adjustments / Deductions" value={totals.totalDeductions} testId="tax-deductions" />
              </div>
              <div className="border-t border-white/30 pt-3">
                <TaxRow label="Estimated Adjusted Business Income" value={totals.estimatedAdjustedBusinessIncome} bold big
                  color={totals.estimatedAdjustedBusinessIncome >= 0 ? "text-emerald-300" : "text-red-300"}
                  testId="tax-adjusted-income" />
              </div>
            </section>

            {/* Breakdown grid */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tax-breakdown">
              <BreakdownCard title="Allowable Business Expenses" value={totals.allowable} testId="brk-allowable" />
              <BreakdownCard title="Non-deductible Expenses" value={totals.nonDeductible} testId="brk-nondeductible" />
              <BreakdownCard title="Partly Business-use Expenses" value={totals.partly} sub="Weighted by business-use %" testId="brk-partly" />
              <BreakdownCard title="Owner Withdrawals" value={totals.ownerDraws} sub="Not a business expense" testId="brk-owner-draws" />
              <BreakdownCard title="Product Purchases" value={totals.productPurchases} sub="Cash view only" testId="brk-product-purchases" />
              <BreakdownCard title="Equipment / Asset Purchases" value={totals.assetPurchases} sub="Capitalised — capital allowance review required" testId="brk-asset-purchases" />
              <BreakdownCard title="Wood Profit-based Incentive" value={totals.woodIncentive} sub="Included in Accounting Net Profit" testId="brk-wood-incentive" />
              <BreakdownCard title="Missing Receipts" value={totals.missingReceiptCount} sub="Count of transactions marked as missing" isCount testId="brk-missing-receipts" />
              <BreakdownCard title="Pending Review Transactions" value={totals.pendingCount} sub="Excluded from all figures until reviewed" isCount testId="brk-pending" />
            </section>

            {/* Tax adjustments */}
            <section className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-3" data-testid="tax-adjustments-block">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-base font-semibold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Tax Adjustments — Assessment Year {year}</h2>
                <Button size="sm" onClick={openNewAdj} className="bg-[#fa9d1c] hover:bg-[#e58a10] text-white rounded-full h-9 px-4 text-xs font-semibold" data-testid="add-adjustment-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Tax Adjustment
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                <strong>Tip:</strong> Enter <span className="text-emerald-700 font-semibold">positive amounts</span> for add-backs (e.g., non-deductible expenses to add back to profit), and <span className="text-red-700 font-semibold">negative amounts</span> for deductions (e.g., capital allowance, private-use adjustment reducing taxable income).
              </p>
              {adjustments.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No tax adjustments recorded for YA {year}.</p>
              ) : (
                <div className="divide-y divide-slate-100" data-testid="adjustments-list">
                  {adjustments.map((a) => {
                    const t = adjTypeById(a.adjustmentTypeId);
                    return (
                      <div key={a.id} className="py-3 flex items-start gap-3" data-testid={`adj-row-${a.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900">{a.description || <span className="italic text-slate-400">No description</span>}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            <span className="font-semibold text-[#193f7d]">{t?.name || "—"}</span>{" · "}{a.adjustmentDate}
                          </p>
                        </div>
                        <p className={`text-sm font-mono font-bold ${a.amount > 0 ? "text-emerald-700" : a.amount < 0 ? "text-red-600" : "text-slate-500"}`}>
                          {a.amount > 0 ? `+${formatCurrency(a.amount)}` : formatCurrency(a.amount)}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button onClick={() => openEditAdj(a)} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 hover:text-[#193f7d]" data-testid={`adj-edit-${a.id}`} aria-label="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setDeleteTarget(a)} className="p-1.5 rounded-full hover:bg-red-50 text-slate-500 hover:text-red-700" data-testid={`adj-delete-${a.id}`} aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 leading-relaxed" data-testid="tax-explanation">
              <FileText className="w-4 h-4 inline-block mr-1.5 text-slate-500 align-text-bottom" />
              <strong>How this is calculated:</strong> Accounting Net Profit = Revenue − Product Cost − Event Expenses − Operating Expenses (all from your events + transactions).
              Estimated Adjusted Business Income = Accounting Net Profit + Add-back Adjustments + Allowable Deductions (adjustments below).
              Owner Withdrawals, Product Purchases and Asset Purchases are shown for reference — they are <strong>not</strong> deducted from Accounting Net Profit.
              Missing Receipts and Pending Review counts flag records that need your attention before filing.
            </div>
          </>
        )}
      </div>

      {/* Adjustment form modal */}
      {showAdjForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center overflow-y-auto p-0 sm:p-6" data-testid="adjustment-form-dialog">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl max-h-[95vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 sm:px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-[#193f7d]" style={{ fontFamily: "Outfit, sans-serif" }}>{adjForm.id ? "Edit Tax Adjustment" : "Add Tax Adjustment"}</h2>
              <button onClick={() => setShowAdjForm(false)} className="p-2 rounded-full hover:bg-slate-100 text-slate-500" data-testid="adj-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-5 sm:px-6 py-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className={labelClass}>Assessment Year *</Label>
                  <Input type="number" min="2000" max="2100" value={adjForm.assessmentYear} onChange={(e) => setAdjField("assessmentYear", e.target.value)} className={inputClass} data-testid="adj-year-input" />
                </div>
                <div>
                  <Label className={labelClass}>Date *</Label>
                  <DatePickerField value={adjForm.adjustmentDate} onChange={(v) => setAdjField("adjustmentDate", v)} placeholder="Select date" testId="adj-date-picker" />
                </div>
              </div>
              <div>
                <Label className={labelClass}>Adjustment Type *</Label>
                <select value={adjForm.adjustmentTypeId} onChange={(e) => setAdjField("adjustmentTypeId", e.target.value)} className={`${inputClass} w-full appearance-none cursor-pointer`} data-testid="adj-type-select">
                  <option value="">Select type…</option>
                  {adjustmentTypes.filter((t) => !t.isArchived).map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                </select>
              </div>
              <div>
                <Label className={labelClass}>Amount (RM) — use positive for add-back, negative for deduction *</Label>
                <Input type="number" step="0.01" value={adjForm.amount} onChange={(e) => setAdjField("amount", e.target.value)} placeholder="e.g. 500 or -1200" className={inputClass} data-testid="adj-amount-input" />
              </div>
              <div>
                <Label className={labelClass}>Description</Label>
                <Input value={adjForm.description} onChange={(e) => setAdjField("description", e.target.value)} placeholder="e.g. Add back private-use utility 20%" className={inputClass} data-testid="adj-description-input" />
              </div>
              <div>
                <Label className={labelClass}>Notes</Label>
                <Textarea value={adjForm.notes} onChange={(e) => setAdjField("notes", e.target.value)} rows={3} placeholder="Optional" className={`${inputClass} h-auto py-2`} data-testid="adj-notes-input" />
              </div>
              <div>
                <Label className={labelClass}>Supporting Document</Label>
                <ReceiptUploader entityType="tax_adjustment" entityId={adjForm.id} />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-slate-200 px-5 sm:px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAdjForm(false)} className="rounded-full" data-testid="adj-cancel">Cancel</Button>
              <Button onClick={saveAdj} disabled={submitting} className="bg-[#193f7d] hover:bg-[#122f5e] text-white rounded-full font-semibold px-6" data-testid="adj-save">
                {submitting ? "Saving…" : adjForm.id ? "Save Changes" : "Save Adjustment"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this tax adjustment?</AlertDialogTitle>
            <AlertDialogDescription>This will not affect Accounting Net Profit but will change the Estimated Adjusted Business Income for {year}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="adj-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const TaxRow = ({ label, value, big = false, bold = false, color, testId }) => (
  <div className="flex items-center justify-between">
    <span className={`text-white/80 ${big ? "text-sm" : "text-xs"} uppercase tracking-wider font-semibold`}>{label}</span>
    <span className={`font-mono ${bold ? "font-bold" : "font-semibold"} ${big ? "text-2xl" : "text-base"} ${color || (typeof value === "number" && value < 0 ? "text-white/85" : "text-white")}`} data-testid={testId}>
      {formatCurrency(value)}
    </span>
  </div>
);

const BreakdownCard = ({ title, value, sub, isCount, testId }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid={testId}>
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{title}</p>
    <p className="text-xl font-mono font-bold text-slate-900 mt-1">
      {isCount ? value : formatCurrency(value)}
    </p>
    {sub && <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

export default TaxPreparation;
